import React from 'react';
import { Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { ToastProvider } from '../../context/ToastContext';
import { getHighestRole, hasDashboardAccess } from '../../api/profile';
import { BurgerButton, useSideDrawer } from '../../shared/ui';
import bmcsLogo from '../../assets/images/logo/bmcs-transparent.png';
import DashboardSidebar from './DashboardSidebar';

const DashboardLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, profile, logout } = useAuth();
  const { open: menuOpen, toggleDrawer, closeDrawer } = useSideDrawer(location.pathname);
  const appVersion = process.env.REACT_APP_VERSION_NUMBER ?? 'dev';

  const handleSignOut = async () => {
    closeDrawer();
    await logout();
    navigate('/');
  };

  if (!user || !profile) {
    return (
      <div className="d-flex justify-content-center align-items-center min-vh-100">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
          <p className="mt-3">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (!hasDashboardAccess(profile)) {
    return <Navigate to="/" replace />;
  }

  return (
    <ToastProvider>
      <div className="dashboard-one-col bg-light">
        <header className="dashboard-navbar">
          <div className="dashboard-navbar__inner">
            <div className="dashboard-navbar__brand">
              <img src={bmcsLogo} alt="BMCS" className="dashboard-navbar__logo" />
              <div className="dashboard-navbar__brand-text">
                <span className="dashboard-navbar__title">BMCS Dashboard</span>
                <span className="dashboard-navbar__subtitle">Management Portal</span>
              </div>
            </div>

            <div className="dashboard-navbar__actions">
              <div className="dashboard-navbar__user-info d-none d-md-flex">
                <span className="dashboard-navbar__user-name">{profile.name}</span>
                <span className="dashboard-navbar__user-role">
                  <i className="bi bi-shield-check" /> {getHighestRole(profile)}
                </span>
              </div>
              <BurgerButton
                open={menuOpen}
                onClick={toggleDrawer}
                controlsId="dashboard-sidebar"
              />
            </div>
          </div>
        </header>

        <DashboardSidebar
          open={menuOpen}
          profile={profile}
          onClose={closeDrawer}
          onSignOut={handleSignOut}
        />

        <main className="dashboard-main pt-4 pe-4 pb-4 ps-0 overflow-x-hidden">
          <Outlet />
        </main>
        <div className="dashboard-version-strip">Version {appVersion}</div>
      </div>
    </ToastProvider>
  );
};

export default DashboardLayout;
